"use client";

import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import BoostIcon from "@/components/common/icons/BoostIcon";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import { getScaledImageUri, ImageScale } from "@/helpers/image.helpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { convertApiDropToExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useDropBoostMutation } from "@/hooks/drops/useDropBoostMutation";
import Image from "next/image";
import Link from "next/link";
import { memo, useCallback, useContext, useMemo, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from "react";
import BoostedDropCardHomeContent from "./BoostedDropCardHomeContent";
type BoostedDropCardHomeVariant = "home" | "chat";
interface BoostedDropCardHomeProps {
  readonly drop: ApiDrop;
  readonly onClick?: () => void;
  readonly variant?: BoostedDropCardHomeVariant;
  readonly rank?: number;
}
const MAX_FIRE_ICONS = 5;
const CARD_CLASSES = "tw-group tw-relative tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/10 tw-bg-black/70 tw-p-0 tw-text-left tw-transition-all tw-duration-500 tw-ease-out hover:-tw-translate-y-1.5 hover:tw-shadow-[0_0_32px_-10px_rgba(255,255,255,0.12)]";
const CARD_BORDER_CLASSES = "tw-pointer-events-none tw-absolute tw-inset-0 tw-z-20 tw-rounded-xl tw-border tw-border-solid tw-border-white/5";
const HEADER_CLASSES = "tw-absolute tw-left-3 tw-right-3 tw-top-3 tw-z-30 tw-flex tw-flex-nowrap tw-items-center tw-justify-between tw-gap-2";
const HEADER_LEFT_CLASSES = "tw-flex tw-min-w-0 tw-items-center tw-gap-2 tw-overflow-hidden";
const PILL_CLASSES = "tw-flex tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-white/10 tw-bg-black/40 tw-px-2.5 tw-py-1 tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] tw-backdrop-blur-md tw-transition-colors group-hover:tw-bg-black/60";
const FOOTER_CLASSES = "tw-relative tw-z-10 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-border-x-0 tw-border-b-0 tw-border-t tw-border-solid tw-border-iron-900 tw-bg-black/70 tw-px-4 tw-py-3";
const AUTHOR_LINK_CLASSES = "tw-flex tw-max-w-full tw-flex-wrap tw-items-center tw-gap-2 tw-no-underline tw-transition-opacity desktop-hover:hover:tw-opacity-80";
const WAVE_LINK_CLASSES = "tw-group/wave tw-flex tw-max-w-full tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-full tw-bg-white/5 tw-py-1 tw-pl-2.5 tw-pr-1 tw-no-underline tw-transition-all active:tw-scale-95 desktop-hover:hover:tw-bg-white/10";
const useCardKeyDown = onClick => {
  const $ = _c(2);
  let t0;
  if ($[0] !== onClick) {
    t0 = event => {
      if (!onClick || event.target !== event.currentTarget || event.key !== "Enter" && event.key !== " ") {
        return;
      }
      event.preventDefault();
      onClick();
    };
    $[0] = onClick;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
const BoostedDropCardChatBoostButton = memo(t0 => {
  const $ = _c(21);
  const {
    drop
  } = t0;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const {
    toggleBoost,
    isPending
  } = useDropBoostMutation();
  let t1;
  if ($[0] !== drop) {
    t1 = convertApiDropToExtendedDrop(drop);
    $[0] = drop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const extendedDrop = t1;
  const isBoosted = drop.context_profile_context?.boosted ?? false;
  let t2;
  if ($[2] !== connectedProfile || $[3] !== drop.id) {
    t2 = !!connectedProfile && !drop.id.startsWith("temp-");
    $[2] = connectedProfile;
    $[3] = drop.id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const canBoost = t2;
  const chatBoostButtonLabel = isBoosted ? "Boosted" : "Boost";
  let t3;
  if ($[5] !== canBoost || $[6] !== extendedDrop || $[7] !== isPending || $[8] !== toggleBoost) {
    t3 = event => {
      event.preventDefault();
      event.stopPropagation();
      if (canBoost && !isPending) {
        toggleBoost(extendedDrop);
      }
    };
    $[5] = canBoost;
    $[6] = extendedDrop;
    $[7] = isPending;
    $[8] = toggleBoost;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleBoostClick = t3;
  const t4 = !canBoost || isPending;
  const t5 = `tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-transition-colors ${canBoost ? "tw-cursor-pointer tw-border-amber-500/20 tw-bg-amber-600/10 hover:tw-bg-amber-600/20" : "tw-cursor-default tw-border-white/10 tw-bg-white/5 tw-text-iron-500"} ${isBoosted && canBoost ? "tw-bg-amber-600/20 tw-text-amber-200" : ""}`;
  const t6 = isBoosted ? "Remove boost" : "Boost";
  const t7 = isBoosted ? "filled" : "outlined";
  let t8;
  if ($[10] !== t7) {
    t8 = <BoostIcon className="tw-size-3.5 tw-flex-shrink-0 tw-text-amber-400" variant={t7} />;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== chatBoostButtonLabel) {
    t9 = <span>{chatBoostButtonLabel}</span>;
    $[12] = chatBoostButtonLabel;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== handleBoostClick || $[15] !== t4 || $[16] !== t5 || $[17] !== t6 || $[18] !== t8 || $[19] !== t9) {
    t10 = <button type="button" onClick={handleBoostClick} onKeyDown={_temp} disabled={t4} className={t5} aria-label={t6}>{t8}{t9}</button>;
    $[14] = handleBoostClick;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
});
BoostedDropCardChatBoostButton.displayName = "BoostedDropCardChatBoostButton";
const BoostedDropCardHeader = memo(t0 => {
  const $ = _c(14);
  const {
    createdAt,
    fireIconsToShow,
    isChatVariant,
    remainingBoosts
  } = t0;
  const t1 = `${HEADER_CLASSES} ${isChatVariant ? "tw-justify-end" : ""}`;
  let t2;
  if ($[0] !== createdAt || $[1] !== isChatVariant) {
    t2 = !isChatVariant && <div className={HEADER_LEFT_CLASSES}><div className={PILL_CLASSES}><span className="tw-text-[10px] tw-font-semibold tw-leading-4 tw-tracking-wide tw-text-iron-300">{getTimeAgoShort(createdAt)}</span></div></div>;
    $[0] = createdAt;
    $[1] = isChatVariant;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== fireIconsToShow) {
    t3 = Array.from({
      length: fireIconsToShow
    }).map(_temp2);
    $[3] = fireIconsToShow;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== remainingBoosts) {
    t4 = remainingBoosts > 0 && <span className="tw-ml-1 tw-text-[10px] tw-font-bold tw-tabular-nums tw-leading-4 tw-text-iron-50">+{remainingBoosts}</span>;
    $[5] = remainingBoosts;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t3 || $[8] !== t4) {
    t5 = <div className={`${PILL_CLASSES} tw-gap-0.5 sm:tw-ml-auto`}>{t3}{t4}</div>;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t5) {
    t6 = <div className={t1}>{t2}{t5}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  return t6;
});
BoostedDropCardHeader.displayName = "BoostedDropCardHeader";
const BoostedDropCardFooter = memo(t0 => {
  const $ = _c(18);
  const {
    author,
    drop,
    isChatVariant,
    waveHref,
    waveName,
    wavePicture
  } = t0;
  const t1 = author.handle ? `/${author.handle}` : "#";
  const t2 = author.handle ?? "User";
  let t3;
  if ($[0] !== author.pfp || $[1] !== t2) {
    t3 = <ProfileAvatar pfpUrl={author.pfp} alt={t2} size={ProfileBadgeSize.SMALL} />;
    $[0] = author.pfp;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = author.handle ?? "Anonymous";
  let t5;
  if ($[3] !== t4) {
    t5 = <span className="tw-break-words tw-text-sm tw-font-medium tw-text-iron-50">{t4}</span>;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t1 || $[6] !== t3 || $[7] !== t5) {
    t6 = <Link href={t1} onClick={_temp3} className={AUTHOR_LINK_CLASSES}>{t3}{t5}</Link>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== drop || $[10] !== isChatVariant || $[11] !== waveHref || $[12] !== waveName || $[13] !== wavePicture) {
    t7 = isChatVariant ? <BoostedDropCardChatBoostButton drop={drop} /> : <Link href={waveHref} onClick={_temp4} className={WAVE_LINK_CLASSES}><span className="tw-break-words tw-text-[11px] tw-font-medium tw-text-iron-400 tw-transition-colors desktop-hover:group-hover/wave:tw-text-iron-200">{waveName}</span>{wavePicture && <Image src={getScaledImageUri(wavePicture, ImageScale.W_AUTO_H_50)} alt={waveName} width={20} height={20} className="tw-size-5 tw-shrink-0 tw-rounded-full tw-object-cover tw-ring-1 tw-ring-white/10" />}</Link>;
    $[9] = drop;
    $[10] = isChatVariant;
    $[11] = waveHref;
    $[12] = waveName;
    $[13] = wavePicture;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6 || $[16] !== t7) {
    t8 = <div className={FOOTER_CLASSES}>{t6}{t7}</div>;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
});
BoostedDropCardFooter.displayName = "BoostedDropCardFooter";
const BoostedDropCardHome = memo(t0 => {
  const $ = _c(24);
  const {
    drop,
    onClick,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "home" : t1;
  const part = drop.parts[0];
  const {
    author,
    wave,
    boosts
  } = drop;
  const fireIconsToShow = Math.min(boosts, MAX_FIRE_ICONS);
  const remainingBoosts = boosts - MAX_FIRE_ICONS;
  const isChatVariant = variant === "chat";
  const handleCardKeyDown = useCardKeyDown(onClick);
  let t2;
  if ($[0] !== wave.id) {
    t2 = getWaveRoute({
      waveId: wave.id,
      isDirectMessage: false,
      isApp: false
    });
    $[0] = wave.id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const waveHref = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className={CARD_BORDER_CLASSES} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== drop.created_at || $[4] !== fireIconsToShow || $[5] !== isChatVariant || $[6] !== remainingBoosts) {
    t4 = <BoostedDropCardHeader createdAt={drop.created_at} fireIconsToShow={fireIconsToShow} isChatVariant={isChatVariant} remainingBoosts={remainingBoosts} />;
    $[3] = drop.created_at;
    $[4] = fireIconsToShow;
    $[5] = isChatVariant;
    $[6] = remainingBoosts;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== isChatVariant || $[9] !== part) {
    t5 = <BoostedDropCardHomeContent isChatVariant={isChatVariant} part={part} />;
    $[8] = isChatVariant;
    $[9] = part;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== author || $[12] !== drop || $[13] !== isChatVariant || $[14] !== wave.name || $[15] !== wave.picture || $[16] !== waveHref) {
    t6 = <BoostedDropCardFooter author={author} drop={drop} isChatVariant={isChatVariant} waveHref={waveHref} waveName={wave.name} wavePicture={wave.picture} />;
    $[11] = author;
    $[12] = drop;
    $[13] = isChatVariant;
    $[14] = wave.name;
    $[15] = wave.picture;
    $[16] = waveHref;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== handleCardKeyDown || $[19] !== onClick || $[20] !== t4 || $[21] !== t5 || $[22] !== t6) {
    t7 = <div role="button" tabIndex={0} onClick={onClick} onKeyDown={handleCardKeyDown} className={CARD_CLASSES}>{t3}{t4}{t5}{t6}</div>;
    $[18] = handleCardKeyDown;
    $[19] = onClick;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  return t7;
});
BoostedDropCardHome.displayName = "BoostedDropCardHome";
export default BoostedDropCardHome;
function _temp(event_0) {
  if (event_0.key === "Enter" || event_0.key === " ") {
    event_0.stopPropagation();
  }
}
function _temp2(_, index) {
  return <BoostIcon key={index} className="tw-size-3 tw-flex-shrink-0 tw-text-orange-400 tw-drop-shadow-sm" variant="filled" />;
}
function _temp3(event) {
  return event.stopPropagation();
}
function _temp4(event_0) {
  return event_0.stopPropagation();
}